import React, { useCallback, useEffect, useState, useRef } from 'react';
import { SummarizePayload } from '../../hooks/useSummary';
import OptionSelector from '../Home/OptionSelector';

const STORAGE_KEY = 'summary_settings';

type Props = {
  isLoading: boolean;
  summarizePayload: SummarizePayload | undefined;
  availableStages: Array<{ id: string; name: string; isDefault: boolean }>;
  availableModels: string[];
  setSummarizePayload: (summarizePayload: SummarizePayload) => void;
};

export const SummaryToolbar = ({
  isLoading,
  summarizePayload,
  setSummarizePayload,
  availableStages,
  availableModels,
}: Props) => {
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o-mini');
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [openModel, setOpenModel] = useState<boolean>(false);
  const [openPrompt, setOpenPrompt] = useState<boolean>(false);
  const initializedRef = useRef(false);

  const saveSettingsToStorage = useCallback((payload: SummarizePayload) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, []);

  const loadSettingsFromStorage = useCallback(() => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings) as SummarizePayload;
        setSelectedModel(parsedSettings.model);
        setSelectedPromptId(parsedSettings.promptId);
        setSummarizePayload(parsedSettings);
        return true;
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
    return false;
  }, [setSummarizePayload]);

  const handleModelChange = useCallback(
    (model: string) => {
      setSelectedModel(model);
      const newPayload = {
        model,
        promptId: selectedPromptId || availableStages[0]?.id || '',
      };
      setSummarizePayload(newPayload);
      saveSettingsToStorage(newPayload);
    },
    [selectedPromptId, availableStages, setSummarizePayload, saveSettingsToStorage]
  );

  const handlePromptChange = useCallback(
    (stage: { id: string; name: string; isDefault: boolean }) => {
      setSelectedPromptId(stage.id);
      const newPayload = {
        model: selectedModel,
        promptId: stage.id,
      };
      setSummarizePayload(newPayload);
      saveSettingsToStorage(newPayload);
    },
    [selectedModel, setSummarizePayload, saveSettingsToStorage]
  );

  useEffect(() => {
    if (initializedRef.current) return;

    const settingsLoaded = loadSettingsFromStorage();

    if (!settingsLoaded && availableStages.length > 0) {
      // Set default values
      const defaultModel = availableModels[0] || 'gpt-4o-mini';
      const defaultPromptId = availableStages.find((stage) => stage.isDefault)?.id || availableStages[0]?.id || '';

      setSelectedModel(defaultModel);
      setSelectedPromptId(defaultPromptId);

      const defaultPayload = {
        model: defaultModel,
        promptId: defaultPromptId,
      };
      setSummarizePayload(defaultPayload);
      saveSettingsToStorage(defaultPayload);
    }

    initializedRef.current = true;
  }, [availableStages, availableModels]);

  if (isLoading) return <div>Nalagam konfiguracijo...</div>;

  // Find the current selected stage for the prompt selector
  const selectedStage = availableStages.find((stage) => stage.id === selectedPromptId);

  return (
    <div className="summary_modal_toolbar">
      <div className="summary_modal_settings">
        <OptionSelector
          opened={openModel}
          title="Model"
          value={selectedModel}
          options={availableModels}
          updateValue={handleModelChange}
          handleOpened={setOpenModel}
          disablePortal={true}
          extractValues={(value) => (value === 'gpt-4o-mini' ? 'GPT-4o Mini' : value === 'gpt-5' ? 'GPT-5' : value)}
        />

        {availableStages.length > 0 && (
          <OptionSelector
            opened={openPrompt}
            title="Predloga"
            value={selectedStage}
            options={availableStages}
            updateValue={handlePromptChange}
            handleOpened={setOpenPrompt}
            disablePortal={true}
            extractValues={(stage) => stage.name}
          />
        )}
      </div>
    </div>
  );
};
